import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Plus, Edit2, Trash2, Eye, EyeOff, ArrowLeft, X } from "lucide-react";
import { Seo } from "./Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { title: "", category: "Financial Tips", excerpt: "", content: "", cover_image: "", published: true };

export const BlogAdmin = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchPosts = () => {
    setLoading(true);
    axios
      .get(`${API}/posts?all=true`)
      .then((res) => setPosts(res.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const openCreate = () => {
    setForm(EMPTY);
    setEditingId(null);
    setError("");
    setShowForm(true);
  };

  const openEdit = (post) => {
    setForm({
      title: post.title,
      category: post.category || "Financial Tips",
      excerpt: post.excerpt || "",
      content: post.content,
      cover_image: post.cover_image || "",
      published: post.published,
    });
    setEditingId(post.id);
    setError("");
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      setError("Title and content are required.");
      return;
    }
    setSaving(true);
    setError("");
    const payload = { ...form, cover_image: form.cover_image.trim() || null };
    try {
      if (editingId) {
        await axios.put(`${API}/posts/${editingId}`, payload);
      } else {
        await axios.post(`${API}/posts`, payload);
      }
      setShowForm(false);
      fetchPosts();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this article permanently?")) return;
    await axios.delete(`${API}/posts/${id}`);
    fetchPosts();
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-80px)] py-12 text-left" data-testid="blog-admin-page">
      <Seo title="Manage Blog | Ashish Ranade Admin" description="Create and manage blog articles." noindex />
      <div className="max-w-6xl mx-auto px-6 lg:px-8 space-y-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#1E3A8A] mb-2">
              <ArrowLeft className="h-4 w-4" /> Back to Dashboard
            </Link>
            <h1 className="text-3xl font-bold text-slate-900">Manage Blog Articles</h1>
            <p className="text-slate-500 text-sm mt-1">Write finance tips to attract visitors from Google.</p>
          </div>
          <button
            onClick={openCreate}
            data-testid="blog-new-button"
            className="inline-flex items-center gap-2 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white px-5 py-3 rounded-lg font-semibold text-sm shadow-md transition-all hover:-translate-y-0.5"
          >
            <Plus className="h-4 w-4" /> New Article
          </button>
        </div>

        {loading ? (
          <p className="text-slate-500">Loading…</p>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            {posts.length === 0 ? (
              <p className="p-8 text-slate-500 text-center">No articles yet. Create your first one!</p>
            ) : (
              <table className="w-full text-left">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="px-6 py-4">Title</th>
                    <th className="px-6 py-4 hidden sm:table-cell">Category</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {posts.map((post) => (
                    <tr key={post.id} data-testid={`blog-admin-row-${post.id}`} className="hover:bg-slate-50/50">
                      <td className="px-6 py-4 font-semibold text-slate-800 max-w-xs">{post.title}</td>
                      <td className="px-6 py-4 hidden sm:table-cell text-sm text-slate-500">{post.category}</td>
                      <td className="px-6 py-4">
                        {post.published ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 px-2.5 py-1 rounded-full">
                            <Eye className="h-3 w-3" /> Published
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                            <EyeOff className="h-3 w-3" /> Draft
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEdit(post)}
                            data-testid={`blog-edit-${post.id}`}
                            className="p-2 text-slate-500 hover:text-[#1E3A8A] hover:bg-slate-100 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(post.id)}
                            data-testid={`blog-delete-${post.id}`}
                            className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-[70] bg-slate-900/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-2xl" data-testid="blog-form-modal">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <h2 className="text-xl font-bold text-slate-900">{editingId ? "Edit Article" : "New Article"}</h2>
              <button onClick={() => setShowForm(false)} className="p-1 text-slate-400 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-5">
              {error && <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg" data-testid="blog-form-error">{error}</p>}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Title *</label>
                <input
                  data-testid="blog-form-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A] outline-none"
                  placeholder="e.g. How to start your first SIP"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Category</label>
                  <input
                    data-testid="blog-form-category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A] outline-none"
                    placeholder="Financial Tips"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Cover Image URL (optional)</label>
                  <input
                    data-testid="blog-form-cover"
                    value={form.cover_image}
                    onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A] outline-none"
                    placeholder="https://…"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Short Summary</label>
                <textarea
                  data-testid="blog-form-excerpt"
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  rows={2}
                  className="w-full border border-slate-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A] outline-none"
                  placeholder="One or two lines shown on the blog card"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Content * (use blank lines for paragraphs)</label>
                <textarea
                  data-testid="blog-form-content"
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  rows={10}
                  className="w-full border border-slate-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A] outline-none font-light"
                  placeholder="Write your article here…"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  data-testid="blog-form-published"
                  checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-[#1E3A8A] focus:ring-[#1E3A8A]"
                />
                Publish immediately (uncheck to save as draft)
              </label>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-lg border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="blog-form-submit"
                  className="px-5 py-2.5 rounded-lg bg-[#1E3A8A] text-white font-semibold text-sm hover:bg-[#1E3A8A]/90 disabled:opacity-60"
                >
                  {saving ? "Saving…" : editingId ? "Update Article" : "Publish Article"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogAdmin;
